import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  CircularProgress,
  Alert,
  Fade,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Chip,
  Drawer,
  InputBase,
  Avatar,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Assignment as AssignmentIcon,
  CloudDone as UptimeIcon,
  CloudUpload as UploadIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  Support as SupportIcon,
  Autorenew as AutorenewIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Storage as ServerIcon,
  PowerSettingsNew as PowerIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useHeartbeat } from '../../contexts/HeartbeatContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../utils/apiUrl';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import api from '../../utils/api';

// Navigation item interface
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section?: string;
}

// TopBar component
const TopBar: React.FC<{
  onMenuToggle: () => void;
  unreadCount: number;
  user: any;
  onNotificationClick: (event: React.MouseEvent<HTMLElement>) => void;
  onLogout: () => void;
  isDarkMode: boolean;
  onThemeToggle: () => void;
}> = ({ onMenuToggle, unreadCount, user, onNotificationClick, onLogout, isDarkMode, onThemeToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  return (
     <AppBar 
       position="fixed" 
       sx={{ 
         zIndex: (theme) => theme.zIndex.drawer + 1,
         width: { lg: 'calc(100% - 240px)' },
         ml: { lg: '240px' },
         backgroundColor: theme.palette.custom.background.header,
         boxShadow: 'none',
         height: '64px',
         '&.MuiAppBar-root': {
           backgroundColor: theme.palette.custom.background.header,
         },
         '&.MuiPaper-root': {
           backgroundColor: theme.palette.custom.background.header,
         },
         '&.MuiAppBar-colorPrimary': {
           backgroundColor: theme.palette.custom.background.header,
         },
         '&.MuiAppBar-colorDefault': {
           backgroundColor: theme.palette.custom.background.header,
         }
       }}
     >
      <Toolbar sx={{ 
        justifyContent: 'space-between', 
        height: '64px',
        backgroundColor: theme.palette.custom.background.admin,
        '&.MuiToolbar-root': {
          backgroundColor: theme.palette.custom.background.header,
        }
      }}>
        {/* Left: Mobile Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMenuToggle}
              sx={{ ml: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Date Display removed */}
        </Box>

        {/* Center: Empty space */}
        <Box sx={{ flex: 1 }} />

        {/* Right: Theme, Notifications, User */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Theme Toggle */}
          <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'transparent',
                border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
                borderRadius: 20,
                padding: 1.5,
                width: 60,
                height: 32,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
              onClick={onThemeToggle}
            >
              {/* Sun Icon - Left Side */}
          <Box
      sx={{
                  position: 'absolute',
                  left: 3,
                  top: '50%',
                  transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
                  width: 20,
                  height: 20,
                  zIndex: 2,
                }}
              >
                <img
                  src="/super-admin/sun-icon.svg"
                  alt="Light Mode"
                  style={{
                    width: '100%',
                    height: '100%',
                    opacity: isDarkMode ? 0.5 : 1,
                    transition: 'opacity 0.3s ease',
                    filter: isDarkMode ? 'none' : 'brightness(0) saturate(100%) invert(33%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
                  }}
                />
          </Box>
              
              {/* Moon Icon - Right Side */}
          <Box
            sx={{
                  position: 'absolute',
                  right: 3,
                  top: '50%',
                  transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
                  width: 20,
                  height: 20,
                  zIndex: 2,
                }}
              >
                <img
                  src="/super-admin/moon-icon.svg"
                  alt="Dark Mode"
                  style={{
                    width: '100%',
                    height: '100%',
                    opacity: !isDarkMode ? 0.5 : 1,
                    transition: 'opacity 0.3s ease',
                    filter: isDarkMode ? 'brightness(0) invert(1)' : 'none'
                  }}
                />
          </Box>
        
               {/* Toggle Switch */}
            <Box
              sx={{
                   position: 'absolute',
                   top: 1,
                   left: isDarkMode ? 30 : 2,
                   width: 28,
                   height: 28,
                   background: isDarkMode ? '#ffffff' : theme.palette.custom.primary.main,
                borderRadius: '50%',
                   boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                   transition: 'left 0.3s ease, background 0.3s ease',
                   zIndex: 3,
                 }}
               />
            </Box>
          </Tooltip>

         {/* Notifications */}
         <Tooltip title="Notifications">
           <IconButton
             onClick={onNotificationClick}
             sx={{ 
               color: isDarkMode ? 'white' : '#202224',
               '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
             }}
           >
             <Badge badgeContent={unreadCount} color="error">
               <Box
                 component="img"
                 src="/super-admin/notification-bell.svg"
                 alt="Notifications"
                 width="24"
                 height="26"
                 sx={{
                   filter: isDarkMode ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(15%) sepia(100%) saturate(2000%) hue-rotate(200deg) brightness(0.7) contrast(1.2)',
                   transition: 'filter 0.3s ease',
                 }}
               />
             </Badge>
           </IconButton>
         </Tooltip>

         {/* User Info */}
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
           <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
             <Typography variant="body2" sx={{ color: isDarkMode ? '#ffffff' : '#202224', fontWeight: 500, lineHeight: 1.2 }}>
               {user?.username || 'Admin'}
             </Typography>
             <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.7)', fontSize: '0.75rem' }}>
               Admin
             </Typography>
           </Box>
        </Box>
        
          {/* Logout */}
          <Tooltip title="Logout">
            <IconButton
              onClick={onLogout}
              sx={{
                color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.7)',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: isDarkMode ? 'white' : '#202224'
                }
              }}
            >
              <PowerIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

// Section Label component
const SectionLabel: React.FC<{ label: string; isDarkMode: boolean }> = ({ label, isDarkMode }) => {
  return (
    <Typography
      variant="caption"
      sx={{
        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.7)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        px: 2,
        py: 1,
        display: 'block',
        fontSize: '0.75rem',
        fontFamily: '"Nunito Sans", sans-serif',
      }}
    >
      {label}
        </Typography>
  );
};

// NavItem component
const NavItemComponent: React.FC<{
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  isDarkMode: boolean;
}> = ({ item, isActive, onClick, isDarkMode }) => {
  const theme = useTheme();
  
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };
  
  return (
    <Box
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="menuitem"
      aria-current={isActive ? "page" : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        position: 'relative',
        backgroundColor: isActive 
          ? (isDarkMode ? 'rgba(255, 255, 255, 0.15)' : theme.palette.custom.primary.main)
          : 'transparent',
        borderRadius: isActive ? '30px' : '0px',
        margin: isActive ? '0 8px' : '0',
        borderLeft: isActive 
          ? 'none'
          : '3px solid transparent',
        '&::before': isActive ? {
          content: '""',
          position: 'absolute',
          left: '-8px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '4px',
          height: '80%',
          backgroundColor: theme.palette.custom.primary.main,
          borderRadius: '0 4px 4px 0',
        } : {},
        '&:hover': !isActive ? {
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(32, 34, 36, 0.05)',
          borderRadius: '30px',
          margin: '0 8px',
        } : {},
        '&:focus': {
          outline: 'none',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Box sx={{ 
        color: isActive ? (isDarkMode ? '#ffffff' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(32, 34, 36, 0.8)'),
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        fontSize: '20px'
      }}>
        {item.icon}
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: isActive ? (isDarkMode ? '#ffffff' : '#ffffff') : (isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(32, 34, 36, 0.8)'),
          fontWeight: isActive ? 600 : 500,
          fontSize: '0.875rem',
          letterSpacing: 'normal',
          fontFamily: '"Nunito Sans", sans-serif',
        }}
      >
        {item.label}
          </Typography>
    </Box>
  );
};

// SideNav component
const SideNav: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
  user: any;
  isDarkMode: boolean;
}> = ({ isOpen, onClose, currentPath, onNavigate, user, isDarkMode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const mainNavItems: NavItem[] = [
    { label: 'Dashboard', path: `/admin/${user?.username}/dashboard`, icon: <DashboardIcon /> },
    { label: 'Clients', path: `/admin/${user?.username}/clients`, icon: <PeopleIcon /> },
    { label: 'Admins', path: `/admin/${user?.username}/admins`, icon: <AdminIcon /> },
    { label: 'Upload', path: `/admin/${user?.username}/upload`, icon: <UploadIcon /> },
    { label: 'History', path: `/admin/${user?.username}/history`, icon: <HistoryIcon /> },
    { label: 'Support', path: `/admin/${user?.username}/support`, icon: <SupportIcon /> },
  ];

  const pageNavItems: NavItem[] = [
    { label: 'Features', path: `/admin/${user?.username}/features`, icon: <SettingsIcon /> },
    { label: 'Whm', path: `/admin/${user?.username}/whm`, icon: <ServerIcon /> },
    { label: 'Email Hub', path: `/admin/${user?.username}/email-hub`, icon: <ServerIcon /> },
    { label: 'Data Maintenance', path: `/admin/${user?.username}/data-maintenance`, icon: <AutorenewIcon /> },
    { label: 'Add Client', path: `/admin/${user?.username}/add-client`, icon: <AddIcon /> },
    { label: 'Add Admin', path: `/admin/${user?.username}/add-admin`, icon: <AddIcon /> },
  ];

  const footerItems: NavItem[] = [
    { label: 'Server Status', path: `/admin/${user?.username}/server-status`, icon: <ServerIcon /> },
    { label: 'Logout', path: '/logout', icon: <PowerIcon /> },
  ];

  const drawerContent = (
    <Box 
      style={{ backgroundColor: theme.palette.custom.background.sidebar }}
      sx={{ 
      width: 240, 
      height: '100%', 
      backgroundColor: theme.palette.custom.background.sidebar,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 'none',
      borderRight: '0.5px solid #E0E0E0',
      '&.MuiBox-root': {
        backgroundColor: `${theme.palette.custom.background.sidebar}`,
      },
      '&.MuiPaper-root': {
        backgroundColor: `${theme.palette.custom.background.sidebar}`,
      }
    }}>
      {/* Logo */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-start', 
        alignItems: 'center', 
        py: 1,
        px: 2,
        height: '64px'
      }}>
        <img
          src={isDarkMode ? "/super-admin/leadforge-dark-logo.svg" : "/super-admin/leadforge-light-logo.svg"}
          alt="LeadForge"
          style={{
            height: '35px',
            width: 'auto',
            objectFit: 'contain',
            transition: 'opacity 0.3s ease',
          }}
        />
      </Box>

      {/* Main Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Box sx={{ py: 2 }}>
          <nav aria-label="Primary">
            {mainNavItems.map((item) => (
              <NavItemComponent
                key={item.path}
                item={item}
                isActive={currentPath === item.path || currentPath.startsWith(item.path)}
                onClick={() => {
                  onNavigate(item.path);
                  if (isMobile) onClose();
                }}
                isDarkMode={isDarkMode}
              />
            ))}
          </nav>
        </Box>

        {/* Pages Section */}
        <Box sx={{ py: 1 }}>
          <SectionLabel label="Pages" isDarkMode={isDarkMode} />
          <nav aria-label="Pages">
            {pageNavItems.map((item) => (
              <NavItemComponent
                key={item.path}
                item={item}
                isActive={currentPath === item.path || currentPath.startsWith(item.path)}
                onClick={() => {
                  onNavigate(item.path);
                  if (isMobile) onClose();
                }}
                isDarkMode={isDarkMode}
              />
            ))}
          </nav>
        </Box>
      </Box>

      {/* Footer Actions */}
      <Box sx={{ borderTop: '0.5px solid #E0E0E0', py: 1 }}>
        <nav aria-label="Footer">
          {footerItems.map((item) => (
            <NavItemComponent
              key={item.path}
              item={item}
              isActive={currentPath === item.path || currentPath.startsWith(item.path)}
              onClick={() => {
                onNavigate(item.path);
                if (isMobile) onClose();
              }}
              isDarkMode={isDarkMode}
            />
          ))}
        </nav>
      </Box>
      </Box>
    );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.custom.background.sidebar,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Box
            sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: 240,
        height: '100vh',
        zIndex: 1200,
        display: { xs: 'none', lg: 'block' },
        backgroundColor: theme.palette.custom.background.sidebar,
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Box>
  );
};

// Main AdminDashboard component
const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useAppTheme();
  const { user, logout } = useAuth();
  const { connectionStatus } = useHeartbeat();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationAnchor, setNotificationAnchor] = useState<HTMLElement | null>(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  // Removed topbar date display, no need to track current date
  const [clickedNotificationIds, setClickedNotificationIds] = useState<Set<number>>(new Set());

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const isClientAdmin = user?.role === 'admin' && user?.companyName;
      
      const supportEndpoint = isClientAdmin
        ? `${getApiUrl()}/api/support/client-admin/notifications`
        : `${getApiUrl()}/api/support/notifications`;
      
      if (isClientAdmin) {
        const assignmentEndpoint = `${getApiUrl()}/api/assignments/notifications`;
        
        const [supportResponse, assignmentResponse] = await Promise.all([
          api.get(supportEndpoint).catch(() => ({ data: { notifications: [], unreadCount: 0 } })),
          api.get(assignmentEndpoint).catch(() => ({ data: { data: { notifications: [], unreadCount: 0 } } }))
        ]);
        
        const supportNotifications = supportResponse.data.notifications || [];
        const assignmentNotifications = assignmentResponse.data.data?.notifications || [];
        
        const allNotifications = [...supportNotifications, ...assignmentNotifications]
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .filter((notification: any) => !clickedNotificationIds.has(notification.id));
        
        // Calculate unread count excluding clicked notifications
        const unreadNotifications = allNotifications.filter((notification: any) => !notification.read);
        const actualUnreadCount = unreadNotifications.length;
        
        setNotifications(allNotifications);
        setUnreadCount(actualUnreadCount);
      } else {
        const supportResponse = await api.get(supportEndpoint)
          .catch(() => ({ data: { notifications: [], unreadCount: 0 } }));
        
        const supportNotifications = supportResponse.data.notifications || [];
        
        const filteredNotifications = supportNotifications
          .filter((notification: any) => !clickedNotificationIds.has(notification.id));
        
        // Calculate unread count excluding clicked notifications
        const unreadNotifications = filteredNotifications.filter((notification: any) => !notification.read);
        const actualUnreadCount = unreadNotifications.length;
        
        setNotifications(filteredNotifications);
        setUnreadCount(actualUnreadCount);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const notificationInterval = setInterval(fetchNotifications, 30000);
    
    return () => {
      clearInterval(notificationInterval);
    };
  }, []);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
    setIsNotificationOpen(true);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
    setIsNotificationOpen(false);
  };

  // Mark notification as read
  const markNotificationAsRead = async (notificationId: number, notificationType: 'support' | 'assignment') => {
    try {
      const token = sessionStorage.getItem('token');
      const isClientAdmin = user?.role === 'admin' && user?.companyName;
      
      let endpoint;
      if (notificationType === 'support') {
        endpoint = isClientAdmin
          ? `${getApiUrl()}/api/support/client-admin/notifications/${notificationId}/read`
          : `${getApiUrl()}/api/support/notifications/${notificationId}/read`;
      } else {
        endpoint = `${getApiUrl()}/api/assignments/notifications/${notificationId}/read`;
      }
      
      await api.put(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle notification item click
  const handleNotificationItemClick = async (notification: any) => {
    // Add to clicked notifications set - this notification will never show again
    setClickedNotificationIds(prev => new Set(Array.from(prev).concat(notification.id)));
    
    // Remove from current list immediately
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    
    // Update unread count if notification was unread
    if (!notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    
    // Close the popover
    handleNotificationClose();
    
    // Navigate based on notification type
    if (notification.assignmentId) {
      navigate(`/admin/${user?.username}/assignments`);
    } else {
      navigate(`/admin/${user?.username}/support`);
    }
    
    // Optionally mark as read on server (but don't wait for it)
    if (!notification.read) {
      const notificationType = notification.assignmentId ? 'assignment' : 'support';
      markNotificationAsRead(notification.id, notificationType).catch(console.error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      
      // Mark each unread notification as read
      for (const notification of unreadNotifications) {
        const notificationType = notification.assignmentId ? 'assignment' : 'support';
        await markNotificationAsRead(notification.id, notificationType);
      }
      
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleNavigate = (path: string) => {
    if (path === '/logout') {
      handleLogout();
    } else {
      navigate(path);
    }
  };

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <SideNav
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPath={location.pathname}
        onNavigate={handleNavigate}
        user={user}
        isDarkMode={isDarkMode}
      />

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', ml: { lg: '240px' } }}>
        {/* Top Bar */}
        <TopBar
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          unreadCount={unreadCount}
          user={user}
          onNotificationClick={handleNotificationClick}
          onLogout={handleLogout}
          isDarkMode={isDarkMode}
          onThemeToggle={toggleTheme}
        />

        {/* Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            mt: '64px',
            backgroundColor: theme.palette.custom.background.default,
            color: isDarkMode ? '#ffffff' : '#202224',
            minHeight: 'calc(100vh - 64px)',
            transition: 'background 0.5s ease-in-out',
            fontFamily: '"Nunito Sans", sans-serif',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Notifications Popover */}
      <Popover
        open={isNotificationOpen}
        anchorEl={notificationAnchor}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Notifications ({unreadCount})
        </Typography>
            {unreadCount > 0 && (
              <Button 
                size="small" 
                variant="text" 
                onClick={markAllAsRead}
                sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
              >
                Mark all read
              </Button>
            )}
      </Box>

          <Divider sx={{ mb: 2 }} />
          
          {notifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No notifications
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.slice(0, 5).map((notification, index) => (
                <React.Fragment key={notification.id || index}>
                  <ListItem 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: theme.palette.action.hover }
                    }}
                    onClick={() => handleNotificationItemClick(notification)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {notification.title || notification.message}
                          </Typography>
                          {!notification.read && (
                            <Chip 
                              label="New" 
                              size="small" 
                              color="primary" 
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                          <Chip 
                            label={notification.assignmentId ? 'Assignment' : 'Support'} 
                            size="small" 
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default AdminDashboard;
